import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Film, Users, Play, UserSquare2 } from "lucide-react";

const short = (label) => {
  const s = String(label || "");
  return s.includes("·") ? s.split("·").pop().trim() : s;
};
const fmtWhen = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return String(d).slice(0, 10); }
};

const Stat = ({ icon: Icon, value, label, tint }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={`veng-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${tint}1a`, color: tint }}><Icon size={18} /></div>
    <div>
      <div className="text-xl font-bold text-slate-900 leading-none num">{value}</div>
      <div className="text-[11px] text-ink-3 mt-1">{label}</div>
    </div>
  </Card>
);

export default function VideoEngagement({ veng, search, copy }) {
  const cat = veng?.videos || [];
  const byVideo = veng?.by_video || {};
  const sponsors = veng?.by_sponsor || [];
  const people = veng?.by_person || [];

  const q = (search || "").trim().toLowerCase();
  const peopleVisible = useMemo(() => {
    const list = people;
    if (!q) return list;
    return list.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.phone || "").includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.sponsor_name || p.sponsor_slug || "").toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veng, q]);

  if (!veng) return <div className="text-center py-16 text-ink-3">Loading video engagement…</div>;
  if (!cat.length) {
    return (
      <Card className="p-10 text-center" data-testid="veng-empty">
        <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center mx-auto mb-3"><Film size={24} /></div>
        <h3 className="text-lg font-semibold">No video watches yet</h3>
        <p className="text-ink-3 text-sm mt-1">Once prospects watch the sizzle, presentation or onboarding videos, they'll show up here by sponsor.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="veng-panel">
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Play} value={veng.totals?.plays || 0} label="Total watches" tint="#4060FF" />
        <Stat icon={Users} value={veng.totals?.people || 0} label="People" tint="#00863A" />
        <Stat icon={UserSquare2} value={veng.totals?.sponsors || 0} label="Sponsors" tint="#8D1CEB" />
        <Stat icon={Film} value={cat.length} label="Videos tracked" tint="#C2185B" />
      </div>

      {/* Grand totals per video */}
      <Card className="p-4" data-testid="veng-by-video-card">
        <h3 className="font-semibold text-slate-900 mb-3 text-sm">Total times each video was watched</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {cat.map((v) => {
            const d = byVideo[v.slot] || { plays: 0, unique: 0 };
            return (
              <div key={v.slot} className="rounded-xl border p-3" style={{ borderColor: "rgba(15,18,38,0.08)" }} data-testid={`veng-video-total-${v.slot}`}>
                <div className="text-[11px] font-semibold text-ink-2 truncate" title={v.label}>{v.label}</div>
                <div className="text-2xl font-bold text-slate-900 num mt-1">{d.plays}</div>
                <div className="text-[10px] text-ink-3">{d.unique} unique {d.unique === 1 ? "watcher" : "watchers"}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* By sponsor */}
      <Card className="p-0 overflow-x-auto" data-testid="veng-by-sponsor-card">
        <div className="px-4 pt-4 pb-2"><h3 className="font-semibold text-slate-900 text-sm">Watches by sponsor</h3>
          <p className="text-[11px] text-ink-3">How many times each sponsor got prospects to watch a video.</p></div>
        <table className="modern members-compact" data-testid="veng-sponsor-table">
          <thead>
            <tr>
              <th>Sponsor</th>
              <th>Wallet name</th>
              <th className="text-right">Total</th>
              <th className="text-right">Unique</th>
              {cat.map((v) => <th key={v.slot} className="text-right whitespace-nowrap" title={v.label}>{short(v.label)}</th>)}
            </tr>
          </thead>
          <tbody>
            {sponsors.length === 0 ? (
              <tr><td colSpan={4 + cat.length} className="text-center py-8 text-ink-3" data-testid="veng-sponsor-empty">No sponsor-attributed watches yet.</td></tr>
            ) : sponsors.map((s) => (
              <tr key={s.sponsor_slug} data-testid={`veng-sponsor-row-${s.sponsor_slug}`}>
                <td className="font-medium text-xs">{s.sponsor_name || <span className="font-mono">{s.sponsor_slug}</span>}</td>
                <td className="text-[11px]" data-testid={`veng-sponsor-wallets-${s.sponsor_slug}`}>
                  {(s.by_wallet || []).length
                    ? (s.by_wallet || []).map((w) => (
                        <span key={w.wallet_name} className="inline-block mr-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-semibold"
                          title="Wallet whose CaryPact link they arrived on">
                          {w.wallet_name}{w.plays > 1 ? ` ×${w.plays}` : ""}
                        </span>
                      ))
                    : <span className="text-ink-3" title="These visits landed before per-wallet link tracking">—</span>}
                </td>
                <td className="text-right font-semibold num">{s.total_plays}</td>
                <td className="text-right num text-ink-2">{s.unique_watchers}</td>
                {cat.map((v) => {
                  const d = s.videos?.[v.slot];
                  return <td key={v.slot} className="text-right num">{d ? d.plays : <span className="text-ink-3">·</span>}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* By person */}
      <Card className="p-0 overflow-x-auto" data-testid="veng-by-person-card">
        <div className="px-4 pt-4 pb-2"><h3 className="font-semibold text-slate-900 text-sm">Who watched ({peopleVisible.length})</h3>
          <p className="text-[11px] text-ink-3">Every person who watched a video — with their sponsor and which videos.</p></div>
        <table className="modern members-compact" data-testid="veng-person-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Phone</th>
              <th>Sponsor</th>
              <th>Wallet name</th>
              <th>Videos watched</th>
              <th className="text-right">Total</th>
              <th className="text-right">Last</th>
            </tr>
          </thead>
          <tbody>
            {peopleVisible.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-ink-3" data-testid="veng-person-empty">No watchers match your search.</td></tr>
            ) : peopleVisible.map((p, i) => (
              <tr key={p.key || i} data-testid={`veng-person-row-${i}`}>
                <td>
                  <div className="font-medium text-xs">{p.name || <span className="text-ink-3 italic">anonymous</span>}</div>
                  {p.email && <div className="text-[11px] text-ink-3 font-mono">{p.email}</div>}
                </td>
                <td className="text-[11px] font-mono">
                  {p.phone ? (
                    <button onClick={() => copy(p.phone)} className="hover:underline text-indigo-700" data-testid={`veng-person-phone-${i}`}>{p.phone}</button>
                  ) : "—"}
                </td>
                <td className="text-[11px]">{p.sponsor_name || (p.sponsor_slug ? <span className="font-mono">{p.sponsor_slug}</span> : "—")}</td>
                <td className="text-[11px]" data-testid={`veng-person-wallet-${i}`}>
                  {p.wallet_name || <span className="text-ink-3" title={p.sponsor_slug ? "This visit landed before per-wallet link tracking" : "No link — direct visit"}>—</span>}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(p.videos || {}).map(([slot, n]) => (
                      <span key={slot} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700" title={(byVideo[slot]?.label) || slot} data-testid={`veng-person-${i}-video-${slot}`}>
                        {short(byVideo[slot]?.label || slot)}{n > 1 && <span className="text-indigo-500">×{n}</span>}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="text-right font-semibold num">{p.total_plays}</td>
                <td className="text-right text-[11px] text-ink-3">{fmtWhen(p.last_watched)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
